import { Link } from 'react-router-dom'
import { Icon } from '../../components/icons/Icon'
import { StatusPill } from '../../components/StatusPill'
import type { ColumnDef } from '../../design-system/list-view/types'
import type { BranchView } from '../../types/branch'

/**
 * Column order per IB-48 design §4.2 (name / legal entity / country / region
 * / PE flag). `Branch` gained a detail page in the CRM-completeness pass
 * (IB-93) — the `name` cell is now a `Link` to `/clients/branches/:id`,
 * mirroring Accounts'/Legal entities' `name` columns; the whole row also
 * activates that same navigation (`onRowActivate`, see
 * `BranchesListScreen`), with "Edit" available as a distinct row action.
 *
 * `legalEntityName` mirrors the `accountName`/`legalEntityName` columns in
 * `screens/cases/caseColumns.tsx`: `sortable: false` because the backend's
 * `RsqlToSpecification` allow-list for Branch (design §2.3) only sorts
 * `{name, country, region, createdAt, id}` — the enriched parent name is not
 * a persisted column. Filtering by legal entity is still available via the
 * `LegalEntityLookupField` facet in `BranchesListScreen` (filters by id).
 *
 * IB-27 S3 retrofit target: `region` is inline-editable (design §4c) when `onEditRegion` is
 * supplied — a plain geographic label, already backend-allow-listed for RSQL sort/filter, clearly
 * non-guarded. `isPermanentEstablishment` is deliberately NOT made inline-editable even though it
 * is not in the `AdjustmentService` guarded catalog: it is a tax-nexus determination and belongs
 * on the detail page with its own context, not a one-click grid toggle (design §4c). Callers that
 * omit `onEditRegion` (e.g. `LegalEntityDetailScreen`'s related-branches list) get the exact same
 * read-only column as before — zero behavior change (AC11).
 */
export interface BranchColumnOptions {
  /** Wired by `BranchesListScreen` to `patchBranch(id, { region }, version)`. Rejects with the
   * server's own `Error`/`ApiError` — `DataGrid` shows its `.message` inline verbatim (AC5). */
  onEditRegion?: (row: BranchView, next: string) => Promise<void>
}

export function buildBranchColumns(options: BranchColumnOptions = {}): ColumnDef<BranchView>[] {
  return [
    {
      id: 'name',
      header: 'Branch',
      width: 220,
      minWidth: 160,
      sortable: true,
      pinnable: true,
      toExportText: (row) => row.name,
      render: (row) => (
        <Link
          to={`/clients/branches/${row.id}`}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--iw-accent)', textDecoration: 'none' }}
        >
          <Icon name="map-pin" size={14} />
          {row.name}
        </Link>
      ),
    },
    {
      id: 'legalEntityName',
      header: 'Legal entity',
      width: 220,
      sortable: false,
      toExportText: (row) => row.legalEntityName ?? '',
      render: (row) => row.legalEntityName ?? <span style={{ color: 'var(--iw-text-subtle)' }}>—</span>,
    },
    {
      id: 'country',
      header: 'Country',
      width: 150,
      sortable: true,
      toExportText: (row) => row.country,
      render: (row) => row.country,
    },
    {
      id: 'region',
      header: 'Region',
      width: 130,
      sortable: true,
      toExportText: (row) => row.region,
      render: (row) => row.region,
      editable: options.onEditRegion
        ? {
            getValue: (row) => row.region,
            commit: (row, next) => options.onEditRegion!(row, next),
          }
        : undefined,
    },
    {
      id: 'isPermanentEstablishment',
      header: 'Permanent establishment',
      width: 180,
      sortable: false,
      toExportText: (row) => (row.isPermanentEstablishment ? 'Yes' : 'No'),
      render: (row) =>
        row.isPermanentEstablishment ? (
          <StatusPill variant="info" label="Yes" />
        ) : (
          <StatusPill variant="neutral" label="No" />
        ),
    },
  ]
}

export const BRANCH_COLUMN_ORDER = ['name', 'legalEntityName', 'country', 'region', 'isPermanentEstablishment']
